import { create } from 'zustand';
import { chatAPI } from '../utils/api';

/**
 * Chat state shared by the sidebar badge, the notifier and the Chat page.
 * One poll loop (started by Layout) keeps everything fresh: every 2.5s while the chat page is open
 * and the tab is visible, every 10s elsewhere, every 30s when the tab is hidden.
 */
const useChatStore = create((set, get) => ({
  conversations: [],           // from /chat/conversations
  messages: {},                // convId -> [msg]
  hasMore: {},                 // convId -> bool
  unread: {},                  // convId -> count
  unreadTotal: 0,
  typing: {},                  // convId -> [names]
  readUpto: {},                // convId -> highest id read by everyone else
  lastId: null,
  since: null,
  activeId: null,              // conversation open on screen
  onChatPage: false,
  listeners: new Set(),        // callbacks for incoming messages (notifier)

  setActive: (id) => set({ activeId: id }),
  setOnChatPage: (v) => set({ onChatPage: v }),
  subscribe: (fn) => { get().listeners.add(fn); return () => get().listeners.delete(fn); },

  loadConversations: async () => {
    const r = await chatAPI.conversations();
    const unread = {}; const readUpto = {};
    r.data.forEach(c => { unread[c.id] = c.unread; readUpto[c.id] = c.read_upto; });
    set(s => ({ conversations: r.data, unread: { ...s.unread, ...unread }, readUpto: { ...s.readUpto, ...readUpto } }));
    return r.data;
  },

  upsertConversation: (conv) => set(s => {
    const rest = s.conversations.filter(c => c.id !== conv.id);
    return { conversations: [conv, ...rest] };
  }),

  removeConversation: (id) => set(s => ({ conversations: s.conversations.filter(c => c.id !== id), activeId: s.activeId === id ? null : s.activeId })),

  loadMessages: async (convId, before) => {
    const r = await chatAPI.messages(convId, before ? { before } : {});
    set(s => {
      const existing = before ? (s.messages[convId] || []) : [];
      const merged = before ? [...r.data.messages, ...existing] : r.data.messages;
      return { messages: { ...s.messages, [convId]: merged }, hasMore: { ...s.hasMore, [convId]: r.data.has_more } };
    });
    return r.data;
  },

  addMessages: (list) => set(s => {
    if (!list.length) return {};
    const messages = { ...s.messages };
    let conversations = [...s.conversations];
    list.forEach(m => {
      const arr = messages[m.conversation_id];
      if (arr && !arr.some(x => x.id === m.id)) messages[m.conversation_id] = [...arr, m];
      const idx = conversations.findIndex(c => c.id === m.conversation_id);
      if (idx >= 0) {
        const c = { ...conversations[idx], last_message: m };
        conversations.splice(idx, 1);
        conversations = [c, ...conversations];
      }
    });
    return { messages, conversations };
  }),

  markDeleted: (ids) => set(s => {
    if (!ids.length) return {};
    const messages = {};
    Object.entries(s.messages).forEach(([cid, arr]) => {
      messages[cid] = arr.map(m => ids.includes(m.id)
        ? { ...m, deleted: true, body: null, file_url: null, waveform: null, ref: null } : m);
    });
    const conversations = s.conversations.map(c => c.last_message && ids.includes(c.last_message.id)
      ? { ...c, last_message: { ...c.last_message, deleted: true, body: null } } : c);
    return { messages, conversations };
  }),

  markRead: async (convId) => {
    const arr = get().messages[convId] || [];
    const last = arr.length ? arr[arr.length - 1].id : 0;
    if (!last || !get().unread[convId]) return;
    set(s => {
      const unread = { ...s.unread, [convId]: 0 };
      return { unread, unreadTotal: Object.values(unread).reduce((a, b) => a + b, 0) };
    });
    try { await chatAPI.read(convId, last); } catch (e) { /* next poll corrects it */ }
  },

  poll: async () => {
    const { lastId, since, conversations } = get();
    const params = {};
    if (lastId !== null) params.after = lastId;
    if (since) params.since = since;
    const r = await chatAPI.poll(params);
    const d = r.data;
    const known = new Set(conversations.map(c => c.id));
    const fresh = d.messages.filter(m => !(get().messages[m.conversation_id] || []).some(x => x.id === m.id));
    get().addMessages(d.messages);
    get().markDeleted(d.deleted || []);
    const unread = {}; Object.entries(d.unread).forEach(([k, v]) => { unread[+k] = v; });
    const readUpto = {}; Object.entries(d.read_upto).forEach(([k, v]) => { readUpto[+k] = v; });
    const typing = {}; Object.entries(d.typing).forEach(([k, v]) => { typing[+k] = v; });
    set({ lastId: d.last_id, since: d.now, unread, unreadTotal: d.unread_total, readUpto, typing });
    // someone added me to a new conversation, or a message arrived for one I don't have listed
    if ((d.conversation_ids || []).some(id => !known.has(id))) {
      try { await get().loadConversations(); } catch (e) { /* ignore */ }
    }
    if (lastId !== null && fresh.length) get().listeners.forEach(fn => { try { fn(fresh); } catch (e) { /* ignore */ } });
    return d;
  },

  reset: () => set({ conversations: [], messages: {}, hasMore: {}, unread: {}, unreadTotal: 0, typing: {}, readUpto: {}, lastId: null, since: null, activeId: null }),
}));

export default useChatStore;
